import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
    Calendar, UtensilsCrossed, Heart, Sparkles, Users, MapPin,
    ChevronRight, HelpCircle, CreditCard, MessageSquare,
    User, ArrowRight, UserPlus, MessageCircle, Sprout, Gem
} from 'lucide-react';
import { HelpInfoModal } from '../components/HelpInfoModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ResidentHomePage() {
    const navigate = useNavigate();
    const { user, profile } = useAuth();
    const { organization, getTerm } = useOrganization();
    const { settings } = useSettings();
    const [upcomingBookings, setUpcomingBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showHelpModal, setShowHelpModal] = useState(false);

    const fetchDashboardData = useCallback(async () => {
        try {
            const token = localStorage.getItem('burston_token');
            if (token) {
                const bookingsRes = await axios.get(`${API}/member/upcoming-bookings`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUpcomingBookings(bookingsRes.data.slice(0, 3));
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Main navigation sections with premium styling
    const mainSections = [
        {
            id: 'events',
            icon: Calendar,
            title: 'Events',
            subtitle: 'Activities & what\'s on',
            bgColor: 'bg-amber-50/80',
            iconBg: 'bg-amber-100',
            iconColor: 'text-amber-700',
            borderColor: 'border-amber-200/60',
            path: '/events'
        },
        {
            id: 'dining',
            icon: UtensilsCrossed,
            title: 'Dining',
            subtitle: 'Menus & reservations',
            bgColor: 'bg-rose-50/80',
            iconBg: 'bg-rose-100',
            iconColor: 'text-rose-700',
            borderColor: 'border-rose-200/60',
            path: '/dining'
        },
        {
            id: 'wellness',
            icon: Heart,
            title: 'Wellness',
            subtitle: 'Health & fitness',
            bgColor: 'bg-emerald-50/80',
            iconBg: 'bg-emerald-100',
            iconColor: 'text-emerald-700',
            borderColor: 'border-emerald-200/60',
            path: '/wellness'
        },
        {
            id: 'services',
            icon: Sparkles,
            title: 'Services',
            subtitle: 'Concierge & requests',
            bgColor: 'bg-sky-50/80',
            iconBg: 'bg-sky-100',
            iconColor: 'text-sky-700',
            borderColor: 'border-sky-200/60',
            path: '/services'
        },
        {
            id: 'community',
            icon: Users,
            title: 'Community',
            subtitle: 'Clubs & noticeboard',
            bgColor: 'bg-violet-50/80',
            iconBg: 'bg-violet-100',
            iconColor: 'text-violet-700',
            borderColor: 'border-violet-200/60',
            path: '/community-hub'
        },
        {
            id: 'local',
            icon: MapPin,
            title: 'Local',
            subtitle: 'Around the area',
            bgColor: 'bg-teal-50/80',
            iconBg: 'bg-teal-100',
            iconColor: 'text-teal-700',
            borderColor: 'border-teal-200/60',
            path: '/local'
        }
    ];

    // Determine if this is Burston or Elysian for appropriate AI assistant label
    const isBurston = organization?.slug === 'burston' || organization?.name?.toLowerCase().includes('burston');
    const assistantLabel = isBurston ? 'Garden Companion' : 'Personal Concierge';
    const assistantDescription = isBurston 
        ? "Burston's gardening expert — ask about our store, seasonal planting tips, or anything garden-related" 
        : 'Your dedicated assistant for dining, wellness, activities and residence enquiries';

    const quickActions = [
        { id: 'book-activity', icon: Calendar, label: 'Book Activity', path: '/activities' },
        { id: 'messages', icon: MessageCircle, label: 'Messages', path: '/messages' },
        { id: 'family-guests', icon: UserPlus, label: 'Family & Guests', path: '/family' },
    ];

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen pb-24" style={{ backgroundColor: '#F9F8F6' }} data-testid="resident-home-page">
            {/* Premium Header */}
            <div className="card-hero px-6 pt-8 pb-14 rounded-b-[2rem]">
                <div className="relative z-10">
                    {/* Top bar */}
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-3">
                            {/* Canopy Logo */}
                            <img 
                                src="/canopy-logo.png"
                                alt="Canopy"
                                className="h-10 w-auto object-contain rounded-lg"
                            />
                            {/* Organization name */}
                            <div className="border-l border-white/30 pl-3">
                                <span 
                                    className="text-lg text-white/90 font-medium"
                                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                                >
                                    {organization?.name || 'Canopy'}
                                </span>
                            </div>
                        </div>
                        <button 
                            onClick={() => setShowHelpModal(true)}
                            className="p-3 hover:bg-white/10 rounded-full transition-colors duration-200 relative flex items-center gap-2"
                            data-testid="help-btn"
                        >
                            <HelpCircle className="w-6 h-6 text-white" />
                            <span className="text-white/80 text-sm font-medium hidden sm:inline">Help</span>
                        </button>
                    </div>
                    
                    {/* Welcome text */}
                    <div className="space-y-1">
                        <p className="text-white/60 text-sm tracking-wide">Good {getGreeting()}</p>
                        <h1 
                            className="text-3xl text-white"
                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                        >
                            Welcome back, {user?.name?.split(' ')[0] || 'Resident'}
                        </h1>
                    </div>

                    {/* Credits Card - Premium style */}
                    {profile && (
                        <div 
                            className="mt-8 bg-white/15 backdrop-blur-sm rounded-2xl p-5 flex items-center justify-between cursor-pointer hover:bg-white/20 transition-all duration-300 border border-white/10"
                            onClick={() => navigate('/member/credits')}
                            data-testid="credits-card"
                        >
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                                    <CreditCard className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <p className="text-white/70 text-sm tracking-wide">Your {getTerm('credits')}</p>
                                    <p 
                                        className="text-4xl text-white mt-0.5"
                                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                    >
                                        {profile.credits_balance}
                                    </p>
                                </div>
                            </div>
                            <ChevronRight className="w-6 h-6 text-white/50" />
                        </div>
                    )}
                </div>
            </div>

            {/* Manager's Message - Elevated */}
            {(settings.manager_message || organization?.manager_message) && (
                <div className="px-5 -mt-5 relative z-10">
                    <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_-4px_rgba(45,74,62,0.1)] border border-[#E5E3DF]">
                        <div className="flex gap-4">
                            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                                <MessageSquare className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="font-medium text-[#2D4A3E] text-sm mb-1">From the Team</p>
                                <p className="text-[#4A4D4C] text-sm leading-relaxed">
                                    {organization?.manager_message || settings.manager_message}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Navigation Grid - Premium Bento */}
            <div className="px-5 mt-6">
                <div className="grid grid-cols-2 gap-4">
                    {mainSections.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => navigate(section.path)}
                            className={`${section.bgColor} ${section.borderColor} border rounded-2xl p-5 text-left transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98]`}
                            data-testid={`nav-${section.id}`}
                        >
                            <div className={`${section.iconBg} w-12 h-12 rounded-xl flex items-center justify-center mb-4`}>
                                <section.icon className={`w-6 h-6 ${section.iconColor}`} />
                            </div>
                            <h3 
                                className="text-lg text-[#2D4A3E] mb-1"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                            >
                                {section.title}
                            </h3>
                            <p className="text-xs text-[#686D6A]">{section.subtitle}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Quick Actions - Pills */}
            <div className="px-5 mt-6">
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {quickActions.map((action) => (
                        <button
                            key={action.id}
                            onClick={() => navigate(action.path)}
                            className="flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 bg-white border border-[#E5E3DF] text-[#2D4A3E] hover:bg-[#2D4A3E]/5 hover:border-[#8A9A86]/40"
                            data-testid={`quick-${action.label.toLowerCase().replace(' ', '-')}`}
                        >
                            <action.icon className="w-4 h-4 text-[#8A9A86]" />
                            {action.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* AI Assistant Card - Prominent */}
            <div className="px-5 mt-6">
                <button
                    onClick={() => navigate('/assistant')}
                    className={`w-full p-5 rounded-2xl bg-white shadow-md border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] ${
                        isBurston 
                            ? 'border-emerald-100'
                            : 'border-stone-200'
                    }`}
                    data-testid="ai-assistant-card"
                >
                    <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                            isBurston ? 'bg-emerald-100' : 'bg-amber-50'
                        }`}>
                            {isBurston ? (
                                <Sprout className="w-7 h-7 text-emerald-600" />
                            ) : (
                                <Gem className="w-7 h-7 text-amber-600" />
                            )}
                        </div>
                        <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                                <h3 className={`text-lg font-semibold ${isBurston ? 'text-emerald-800' : 'text-stone-800'}`}>
                                    {assistantLabel}
                                </h3>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                    isBurston ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                    24/7
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                                {assistantDescription}
                            </p>
                        </div>
                        <ChevronRight className={`w-5 h-5 ${isBurston ? 'text-emerald-400' : 'text-stone-400'}`} />
                    </div>
                </button>
            </div>

            {/* Upcoming Bookings */}
            {upcomingBookings.length > 0 && (
                <div className="px-5 mt-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 
                            className="text-xl text-[#2D4A3E]"
                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                        >
                            Coming Up
                        </h2>
                        <button 
                            onClick={() => navigate('/member/bookings')}
                            className="text-sm text-[#8A9A86] font-medium flex items-center gap-1 hover:text-[#2D4A3E] transition-colors"
                        >
                            View all <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="space-y-3">
                        {upcomingBookings.map((booking) => (
                            <div 
                                key={booking.id}
                                className="bg-white rounded-2xl p-4 border border-[#E5E3DF] flex items-center gap-4 hover:shadow-md transition-all duration-200 cursor-pointer"
                                onClick={() => navigate('/member/bookings')}
                            >
                                <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                                    <Calendar className="w-6 h-6 text-amber-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-[#2D4A3E] truncate">
                                        {booking.activity?.title || 'Activity'}
                                    </h3>
                                    <p className="text-sm text-[#686D6A] mt-0.5">
                                        {booking.activity?.date_time ? formatDate(booking.activity.date_time) : 'Date TBC'}
                                    </p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-[#8A9A86]" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Welcome Guide CTA - Premium */}
            <div className="px-5 mt-8 mb-6">
                <div 
                    className="relative overflow-hidden rounded-2xl p-6 text-white cursor-pointer hover:shadow-xl transition-all duration-300"
                    style={{ 
                        background: 'linear-gradient(135deg, #2D4A3E 0%, #3A5F50 50%, #4A7060 100%)'
                    }}
                    onClick={() => navigate('/welcome-guide')}
                    data-testid="welcome-guide-cta"
                >
                    {/* Texture overlay */}
                    <div 
                        className="absolute inset-0 opacity-[0.04]"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                        }}
                    />
                    <div className="relative z-10 flex items-center justify-between">
                        <div>
                            <h3 
                                className="text-xl mb-2 text-amber-200"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                            >
                                Welcome Guide
                            </h3>
                            <p className="text-white/80 text-sm">
                                Discover everything about {organization?.name || 'your residence'}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-amber-400/20 flex items-center justify-center">
                            <ArrowRight className="w-6 h-6 text-amber-200" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Help Info Modal */}
            <HelpInfoModal 
                isOpen={showHelpModal} 
                onClose={() => setShowHelpModal(false)} 
                organization={organization}
            />
        </div>
    );
}

function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
}

export default ResidentHomePage;
